import { useState, useEffect } from "react";
import { useKanban } from "./kanban-provider";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from "@mui/material";


const colorOptions = [
  { value: "bg-blue-500", label: "Blue", cssColor: "#3b82f6" },
  { value: "bg-green-500", label: "Green", cssColor: "#22c55e" },
  { value: "bg-yellow-500", label: "Yellow", cssColor: "#eab308" },
  { value: "bg-red-500", label: "Red", cssColor: "#ef4444" },
  { value: "bg-purple-500", label: "Purple", cssColor: "#8b5cf6" },
  { value: "bg-pink-500", label: "Pink", cssColor: "#ec4899" },
  { value: "bg-indigo-500", label: "Indigo", cssColor: "#6366f1" },
  { value: "bg-orange-500", label: "Orange", cssColor: "#f97316" },
];


export default function ColumnModal({ isOpen, onClose, column }) {
  const { addColumn, updateColumn } = useKanban();
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("bg-blue-500");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (column) {
        setTitle(column.title);
        setColor(column.color); // Backend stores bg-blue-500
      } else {
        setTitle("");
        setColor("bg-blue-500");
      }
      setError("");
    }
  }, [isOpen, column]);

  const handleSubmit = () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    const columnData = {
      title,
      color,
    };

    if (column) {
      updateColumn({ ...columnData, id: column.id });
    } else {
      addColumn(columnData);
    }

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 425 } }}
    >
      <DialogTitle>{column ? "Edit Column" : "Create New Column"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        <FormControl>
          <FormLabel>Title</FormLabel>
          <TextField
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Column title"
            fullWidth
            variant="outlined"
            size="small"
            error={!!error && !title.trim()}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Color</FormLabel>
          <RadioGroup
            value={color}
            onChange={(e) => setColor(e.target.value)}
            sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}
          >
            {colorOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio sx={{ display: "none" }} />}
                label={
                  <Box
                    sx={{
                      width: "100%",
                      height: 32,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: color === option.value ? "2px solid" : "2px solid transparent",
                      borderColor: color === option.value ? "primary.main" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        bgcolor: option.cssColor,
                      }}
                    />
                  </Box>
                }
                sx={{ m: 0 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button variant="outlined" onClick={handleSubmit}>
          {column ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}